import React, { useEffect, useState } from "react";
import moment from "moment";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";
import { ILocation, ISegment } from "redux/reducers/models/reservations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faCircleCheck, faCircleDollar, faFile, faFlag, faPlateUtensils, faTrophy, faUsers, IconName } from "@fortawesome/pro-regular-svg-icons";
import { useDrag } from "react-dnd";
import "./bookedSegment.scss";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { BOOKED_SEGMENT_DROP_TYPE, MoveBookingToDropSegmentAction, useDropBookedSegment } from "./useDropBookedSegment";
import { TCustomerNote } from "../../customer/context/contextTypes";

interface IBookedSegmentProps {
  cellClassNames: string;
  cellContentClassNames: string;
  rowSpan: number;
  dimmed?: boolean;
  segment: ISegment;
  segmentCustomerNotes?: TCustomerNote;
  startingSegment: ISegment;
  location: ILocation;
  availableDropHours: number;
  bookedHours: number;
  draggingBookingId: number;
  setDraggingBookingId: (bookingId: number) => void;
  moveBookingToDropSegment: MoveBookingToDropSegmentAction;
  openEditBookingModal: (segment: ISegment, location: ILocation) => Promise<void>;
  updateCheckInStatus: (status: string, segment: ISegment) => Promise<void>;
  setBlockedType: (segment: ISegment, status: string) => Promise<void>;
  updateFinancialStatus: (status: string, segment: ISegment) => Promise<void>;
  viewAuditTrail: (segmentId: number) => void;
  openTableSelectionModal: (segment: ISegment) => void;
  openViewTableModal: (tableId: number) => void;
  acceptBooking: (segment: ISegment) => Promise<void>;
  declineBooking: (segment: ISegment) => Promise<void>;
}

function BookingStatusIcons(props: { segment: ISegment }) {
  const { segment } = props;

  return (
    <>
      {segment?.booking?.has_notes ? <FontAwesomeIcon icon={faFile} className="booking-card-icon" /> : null}
      {segment?.booking?.order_financial_status === "paid" ? (
        <FontAwesomeIcon icon={faCircleDollar} className="booking-card-icon booking-card-icon--paid" />
      ) : null}
      {segment?.booking?.check_in_status === "checked" ? (
        <FontAwesomeIcon icon={faCircleCheck} className="booking-card-icon booking-card-icon--checked" />
      ) : null}
      {segment?.booking?.check_in_status === "no_show" ? (
        <FontAwesomeIcon icon={faBan} className="booking-card-icon booking-card-icon--no-show" />
      ) : null}
      {segment?.booking?.table_id ? <FontAwesomeIcon icon={faPlateUtensils} className="booking-card-icon" /> : null}
    </>
  );
}

export interface IBookedSegmentItem {
  segment: ISegment;
  startingSegment: ISegment;
  location: ILocation;
  bookedHours: number;
}

export default function BookedSegment(props: IBookedSegmentProps) {
  const {
    cellClassNames,
    cellContentClassNames,
    rowSpan,
    dimmed,
    segment,
    startingSegment,
    location,
    availableDropHours,
    bookedHours,
    draggingBookingId,
    setDraggingBookingId,
    moveBookingToDropSegment,
    openEditBookingModal,
    updateCheckInStatus,
    updateFinancialStatus,
    viewAuditTrail,
    openTableSelectionModal,
    openViewTableModal,
    acceptBooking,
    declineBooking,
  } = props;

  const isPending = segment?.booking?.status === "pending";
  const compact = rowSpan <= 1;
  const customerName =
    segment?.booking?.customer?.full_name ||
    [segment?.booking?.first_name, segment?.booking?.last_name].filter(Boolean).join(" ");
  const customerType = segment?.booking?.customer?.customer_type;
  const bookingTone = isPending
    ? "pending"
    : segment?.booking?.check_in_status === "no_show"
      ? "no-show"
      : segment?.booking?.order_financial_status === "paid"
        ? "paid"
        : segment?.booking?.check_in_status === "checked"
          ? "checked"
          : "default";

  function formatClock(time?: string) {
    if (!time) {
      return "";
    }
    return moment(time, ["HH:mm:ss", "HH:mm"]).format("hh:mm A");
  }

  const startLabel = formatClock(segment?.booking?.start_time || segment?.start_time);
  const endLabel = formatClock(segment?.booking?.end_time);
  const timeLabel = endLabel ? `${startLabel} – ${endLabel}` : startLabel;

  const useDropBookedSegmentState = useDropBookedSegment(
    segment,
    location,
    availableDropHours,
    moveBookingToDropSegment,
  );

  // Set to true before browser drag ghost image is created. isDragging is true after.
  const [dragBeganStarting, setDragBeganStarting] = useState<boolean>(false);
  const { t } = useTranslation();
  const eventName = segment?.league_id
    ? segment.league_name || t("secure.facility.reservation.reservation.013")
    : segment?.tournament_id
      ? segment.tournament_name || t("secure.facility.reservation.reservation.014")
      : "";
  const isEventBooking = Boolean(eventName);
  const [{ isDragging }, drag] = useDrag(() => {
    const item: IBookedSegmentItem = {
      segment,
      startingSegment,
      location,
      bookedHours,
    };

    return {
      item,
      type: BOOKED_SEGMENT_DROP_TYPE,
      canDrag: () => !isPending,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    };
  }, [segment, startingSegment, location, bookedHours, isPending]);

  useEffect(() => {
    if (isDragging) {
      setDraggingBookingId(segment?.booking_id);
    } else {
      setDraggingBookingId(undefined);
    }
  }, [isDragging]);

  function getBookedSegmentCellStyles() {
    const segmentInDraggedBooking = draggingBookingId === segment.booking_id;

    return {
      ...(dragBeganStarting
        ? {
            borderBottom: "none",
            borderLeft: "none",
            borderTop: "none",
            borderRight: "none",
          }
        : {}),
      ...(segmentInDraggedBooking
        ? {
            borderBottom: "none",
            borderLeft: "none",
            borderTop: "none",
            borderRight: "1px solid black",
            opacity: "0.7",
          }
        : {}),
      ...(segmentInDraggedBooking && useDropBookedSegmentState.isOver
        ? {
            boxShadow: `0px 0px 0px 1px ${useDropBookedSegmentState.canDrop ? "green" : "red"} inset`,
          }
        : {}),
    };
  }

  return (
    <>
      <td
        ref={el => useDropBookedSegmentState.drop(drag(el))}
        rowSpan={rowSpan}
        data-booking-id={segment.booking_id}
        className={classNames(cellClassNames, "booked-segment-cell", {
          "segment-slot--span": rowSpan > 1,
        })}
        onClick={() => void openEditBookingModal(segment, location)}
        style={getBookedSegmentCellStyles()}
        onDragStart={() => setDragBeganStarting(true)}
        onDragEnd={() => setDragBeganStarting(false)}
      >
        {dragBeganStarting && (
          <div className="booked-segment-cell-booked-hours-badge">{`${bookedHours} hr${
            bookedHours > 1 ? "s" : ""
          }`}</div>
        )}
        <div className="segment-booking-frame" style={{ height: rowSpan * 52 }}>
        <RightClickMenu
          fullHeightWidth
          sections={[
            ...(isPending
              ? [
                  [
                    {
                      icon: "circle-check" as IconName,
                      title: t("Approve Booking"),
                      action: () => void acceptBooking(segment),
                    },
                    {
                      icon: "ban" as IconName,
                      title: t("Decline Request"),
                      action: () => void declineBooking(segment),
                    },
                  ],
                ]
              : []),
            [
              {
                icon: "circle-check",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.booked_segment.001"),
                action: () => void updateCheckInStatus("checked", segment),
                disabled: isPending || segment.booking.check_in_status === "checked",
              },
              {
                icon: "circle",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.booked_segment.002"),
                action: () => void updateCheckInStatus("unchecked", segment),
                disabled: isPending || segment.booking.check_in_status === "unchecked",
              },
              {
                icon: "ban",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.booked_segment.003"),
                action: () => void updateCheckInStatus("no_show", segment),
                disabled: isPending || segment.booking.check_in_status === "no_show",
              },
            ],
            [
              {
                icon: "circle-dollar",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.booked_segment.004"),
                action: () => void updateFinancialStatus("paid", segment),
                disabled: isPending || segment.booking.order_financial_status === "paid",
              },
              {
                icon: "user-slash",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.booked_segment.005"),
                action: () => void updateFinancialStatus("unpaid", segment),
                disabled: isPending || segment.booking.order_financial_status === "unpaid",
              },
            ],
            [
              {
                icon: "list",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.booked_segment.006"),
                action: () => viewAuditTrail(segment.id),
              },
            ],
            [
              ...(!segment?.booking?.table_id ? [
                {
                  icon: "plate-utensils" as IconName,
                  title: t("Check In & Open Table"),
                  action: () => openTableSelectionModal(segment),
                  disabled: isPending,
                },
              ] : [
                {
                  icon: "plate-utensils" as IconName,
                  title: t("View Table"),
                  action: () => openViewTableModal(segment?.booking?.table_id),
                },
              ]),
            ],
          ]}
        >
          <div className={classNames(cellContentClassNames, "booking-card", `booking-card--${bookingTone}`, {
            "booking-card--compact": compact,
            "booking-card--event": isEventBooking,
            "booking-card--dimmed": dimmed,
          })}>
            {compact ? (
              <>
                <p className="booking-card-name">
                  {customerName}
                  {segment?.booking?.customer?.has_high_priority_notes ? (
                    <FontAwesomeIcon className="booking-card-alert" icon={["far", "circle-exclamation"]} />
                  ) : null}
                </p>
                {isEventBooking ? (
                  <span className="booking-card-event">
                    <FontAwesomeIcon icon={segment.league_id ? faTrophy : faFlag} />
                    <span>{eventName}</span>
                  </span>
                ) : null}
                {!dragBeganStarting && (
                  <div className="booking-card-meta">
                    <BookingStatusIcons segment={segment} />
                    <span className="booking-card-quantity">
                      <FontAwesomeIcon icon={faUsers} className="booking-card-icon" />
                      {segment?.booking?.quantity}
                    </span>
                  </div>
                )}
              </>
            ) : isEventBooking ? (
              <div className="booking-card-body">
                <div className="booking-card-heading">
                  <div className="booking-card-title-row">
                    <p className="booking-card-name">
                      {customerName}
                      {segment?.booking?.customer?.has_high_priority_notes ? (
                        <FontAwesomeIcon className="booking-card-alert" icon={["far", "circle-exclamation"]} />
                      ) : null}
                    </p>
                    {!dragBeganStarting && (
                      <div className="booking-card-title-meta">
                        <BookingStatusIcons segment={segment} />
                        <span className="booking-card-quantity">
                          <FontAwesomeIcon icon={faUsers} className="booking-card-icon" />
                          {segment?.booking?.quantity}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="booking-card-event">
                    <FontAwesomeIcon icon={segment.league_id ? faTrophy : faFlag} />
                    <span>{eventName}</span>
                  </span>
                  {isPending ? <span className="booking-card-badge booking-card-badge--pending">{t("Pending")}</span> : null}
                </div>
                <p className="booking-card-time">{timeLabel}</p>
              </div>
            ) : (
              <>
                <div className="booking-card-body">
                  <div className="booking-card-heading">
                    <p className="booking-card-name">
                      {customerName}
                      {segment?.booking?.customer?.has_high_priority_notes ? (
                        <FontAwesomeIcon className="booking-card-alert" icon={["far", "circle-exclamation"]} />
                      ) : null}
                    </p>
                    {customerType ? <span className={`booking-card-badge booking-card-badge--${bookingTone}`}>{customerType}</span> : null}
                    {isPending ? <span className="booking-card-badge booking-card-badge--pending">{t("Pending")}</span> : null}
                  </div>
                  <p className="booking-card-time">{timeLabel}</p>
                </div>
                {!dragBeganStarting && (
                  <div className="booking-card-side">
                    <span className="booking-card-quantity">
                      <FontAwesomeIcon icon={faUsers} className="booking-card-icon" />
                      {segment?.booking?.quantity}
                    </span>
                    <div className="booking-card-icons">
                      <BookingStatusIcons segment={segment} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </RightClickMenu>
        </div>
      </td>
      {useDropBookedSegmentState.dropConfirmationPopup}
    </>
  );
}
